// ProductManager.js - Hantera produktkatalog per klinik
import React, { useState, useEffect, useContext } from 'react';
import { UserContext } from '../../UserContext';
import { getProducts, createProduct, updateProduct, deleteProduct } from './calendarApi';
import { isClinicLevelAdmin } from '../../utils/permissions';
import './ProductManager.css';

const VAT_RATES = [
  { value: 0, label: '0%' },
  { value: 6, label: '6%' },
  { value: 12, label: '12%' },
  { value: 25, label: '25%' }
];

const ProductManager = () => {
  const { user } = useContext(UserContext);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    vat_rate: 25,
    is_friskvard: false,
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadProducts();
  }, [showInactive]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await getProducts(!showInactive);
      setProducts(data);
    } catch (err) {
      setError('Kunde inte ladda produkter');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        vat_rate: product.vat_rate,
        is_friskvard: product.is_friskvard,
        is_active: product.is_active
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        vat_rate: 25,
        is_friskvard: false,
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.price) {
      setError('Namn och pris krävs');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        vat_rate: parseFloat(formData.vat_rate),
        is_friskvard: formData.is_friskvard,
        is_active: formData.is_active
      };

      if (editingProduct) {
        await updateProduct(editingProduct.product_id, productData);
      } else {
        await createProduct(productData);
      }

      handleCloseModal();
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte spara produkt');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Vill du inaktivera produkten "${product.name}"?`)) {
      return;
    }

    try {
      await deleteProduct(product.product_id);
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte ta bort produkt');
    }
  };

  const handleToggleActive = async (product) => {
    try {
      await updateProduct(product.product_id, { is_active: !product.is_active });
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Kunde inte uppdatera produkt');
    }
  };

  // Kontrollera admin-behörighet (global admin eller klinikadmin)
  if (!isClinicLevelAdmin(user)) {
    return (
      <div className="product-manager">
        <div className="access-denied">
          <i className="fas fa-lock"></i>
          <h2>Åtkomst nekad</h2>
          <p>Endast administratörer kan hantera produkter.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="product-manager">
      <div className="product-manager-header">
        <h1><i className="fas fa-box"></i> Produktkatalog</h1>
        <div className="header-actions">
          <label className="show-inactive-toggle">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Visa inaktiva
          </label>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            <i className="fas fa-plus"></i> Ny produkt
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <i className="fas fa-spinner fa-spin"></i> Laddar produkter...
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-box-open"></i>
          <p>Inga produkter hittades</p>
          <button className="btn btn-primary" onClick={() => handleOpenModal()}>
            Skapa din första produkt
          </button>
        </div>
      ) : (
        <div className="products-table-container">
          <table className="products-table">
            <thead>
              <tr>
                <th>Produkt</th>
                <th>Beskrivning</th>
                <th>Pris</th>
                <th>Moms</th>
                <th>Friskvård</th>
                <th>Status</th>
                <th>Åtgärder</th>
              </tr>
            </thead>
            <tbody>
              {products.map(product => (
                <tr key={product.product_id} className={!product.is_active ? 'inactive' : ''}>
                  <td className="product-name">{product.name}</td>
                  <td className="product-description">{product.description || '-'}</td>
                  <td className="product-price">{product.price.toFixed(2)} kr</td>
                  <td className="product-vat">{product.vat_rate}%</td>
                  <td className="product-friskvard">
                    {product.is_friskvard ? (
                      <span className="badge badge-friskvard">
                        <i className="fas fa-heart"></i> Ja
                      </span>
                    ) : (
                      <span className="badge badge-no">Nej</span>
                    )}
                  </td>
                  <td className="product-status">
                    <span className={`status-badge ${product.is_active ? 'active' : 'inactive'}`}>
                      {product.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="product-actions">
                    <button
                      className="btn-icon"
                      onClick={() => handleOpenModal(product)}
                      title="Redigera"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleToggleActive(product)}
                      title={product.is_active ? 'Inaktivera' : 'Aktivera'}
                    >
                      <i className={`fas ${product.is_active ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal för att skapa/redigera produkt */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content product-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProduct ? 'Redigera produkt' : 'Ny produkt'}</h2>
              <button className="close-btn" onClick={handleCloseModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error">{error}</div>}

                <div className="form-group">
                  <label>Produktnamn *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="T.ex. Massage 60 min"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Beskrivning</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Valfri beskrivning"
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Pris (inkl. moms) *</label>
                    <div className="input-with-suffix">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price}
                        onChange={(e) => setFormData({...formData, price: e.target.value})}
                        placeholder="0.00"
                        required
                      />
                      <span className="suffix">kr</span>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Momssats</label>
                    <select
                      value={formData.vat_rate}
                      onChange={(e) => setFormData({...formData, vat_rate: parseFloat(e.target.value)})}
                    >
                      {VAT_RATES.map(rate => (
                        <option key={rate.value} value={rate.value}>{rate.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={formData.is_friskvard}
                        onChange={(e) => setFormData({...formData, is_friskvard: e.target.checked})}
                      />
                      <span className="checkbox-label">
                        <i className="fas fa-heart"></i> Friskvårdsprodukt
                      </span>
                    </label>
                    <small>Markeras som friskvård på kvittot</small>
                  </div>

                  {editingProduct && (
                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                        />
                        <span className="checkbox-label">Aktiv produkt</span>
                      </label>
                      <small>Inaktiva produkter visas ej vid försäljning</small>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? (
                    <><i className="fas fa-spinner fa-spin"></i> Sparar...</>
                  ) : (
                    editingProduct ? 'Spara ändringar' : 'Skapa produkt'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductManager;
